// Data-driven weapon definitions. Every value here is balance-tunable config;
// the weapon SYSTEMS (src/game/systems/weapons.ts) read these at runtime.
export type WeaponBehavior =
  | "zap" // instant arc to N nearest enemies in range
  | "projectile" // fires bolts toward nearest enemy
  | "chain" // bolt that jumps between enemies
  | "beam" // piercing line
  | "burst" // shotgun spread
  | "orbit" // future: orbiting nodes
  | "mine"; // future: area denial

export interface WeaponDef {
  id: string;
  name: string;
  behavior: WeaponBehavior;
  description: string;
  color: string;
  /** Seconds between activations (before attackSpeed multipliers). */
  cooldown: number;
  damage: number;
  range: number;
  /** Targets hit per activation / projectiles fired / chain jumps. */
  count: number;
  projectileSpeed: number;
  /** Enemies a single projectile/beam passes through. */
  pierce: number;
  /** Total spread arc in radians for multi-shot weapons. */
  spread: number;
  critChance: number;
  critMult: number;
  /** Optional knockback impulse applied on hit. */
  knockback: number;
}

export const WEAPONS: Record<string, WeaponDef> = {
  pulse_zap: {
    id: "pulse_zap",
    name: "Pulse Zap",
    behavior: "zap",
    description: "Balanced AOE. Auto-zaps the nearest corruption.",
    color: "#76D2DB",
    cooldown: 0.6,
    damage: 14,
    range: 220,
    count: 1,
    projectileSpeed: 0,
    pierce: 0,
    spread: 0,
    critChance: 0.08,
    critMult: 2,
    knockback: 40,
  },
  piercing_beam: {
    id: "piercing_beam",
    name: "Piercing Beam",
    behavior: "beam",
    description: "Long-range penetration. Punches through a line of enemies.",
    color: "#A6E9F0",
    cooldown: 1.1,
    damage: 22,
    range: 520,
    count: 1,
    projectileSpeed: 1400,
    pierce: 6,
    spread: 0,
    critChance: 0.12,
    critMult: 2.2,
    knockback: 20,
  },
  packet_burst: {
    id: "packet_burst",
    name: "Packet Burst",
    behavior: "burst",
    description: "Shotgun spray of packets. Devastating up close.",
    color: "#E68A4E",
    cooldown: 0.9,
    damage: 9,
    range: 260,
    count: 6,
    projectileSpeed: 760,
    pierce: 1,
    spread: 0.7,
    critChance: 0.06,
    critMult: 2,
    knockback: 60,
  },
  chain_pulse: {
    id: "chain_pulse",
    name: "Chain Pulse",
    behavior: "chain",
    description: "Lightning that leaps between targets.",
    color: "#BED4CB",
    cooldown: 1.0,
    damage: 12,
    range: 240,
    count: 4,
    projectileSpeed: 0,
    pierce: 0,
    spread: 0,
    critChance: 0.1,
    critMult: 2,
    knockback: 10,
  },
  data_lance: {
    id: "data_lance",
    name: "Data Lance",
    behavior: "projectile",
    description: "High single-target damage. Fast, precise bolts.",
    color: "#EAD49A",
    cooldown: 0.45,
    damage: 26,
    range: 460,
    count: 1,
    projectileSpeed: 1100,
    pierce: 2,
    spread: 0,
    critChance: 0.18,
    critMult: 2.5,
    knockback: 30,
  },
};

export const STARTING_WEAPON = "pulse_zap";
